import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  SERVICE_ITEM_CATEGORIES,
  SPLIT_METHODS,
  basisPointsFromPercent,
  type ServiceItemCategory,
  type SplitMethod,
} from '@wodi/domain';
import type {
  PayerView,
  QuoteLineView,
  ServiceItemView,
  SplitRuleView,
  SplitTermsView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  Row,
  SearchPicker,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  assignmentsQuery,
  changeSplitRule,
  createPayer,
  createScheme,
  listItems,
  payerQuery,
  payersQuery,
  priceListsQuery,
  putAssignment,
  quote,
  reviewScheme,
  splitRulesQuery,
} from '../api/finance.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  CATEGORY_LABELS,
  METHOD_LABELS,
  PAYER_KIND_LABELS,
  day,
  describeTerms,
  money,
} from '../finance/labels.ts';
import { SchemeBenefits } from '../insurance/benefit-rules.tsx';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Payers (docs/plans/phase1-finance-catalogue.md §6.3): who pays, which price
// list applies to them, and who pays what. SHA's split is not set up by Wodi
// (TODO(verify-spec), decision 6): it stays "not set up" until someone enters it.

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const EVERY_SCHEME = 'every';
const EVERY_CATEGORY = 'every';

function AddPayer({ onDone }: { onDone: (payerId: string | null) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'insurer' | 'corporate' | 'other' | null>(null);
  const [tried, setTried] = useState(false);
  const add = useMutation({
    mutationFn: () => createPayer({ kind: kind ?? 'other', name: name.trim() }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: payersQuery.queryKey });
      toasts.show({ title: 'Payer added', description: created.name });
      onDone(created.id);
    },
  });
  return (
    <form
      className={styles.inlineForm}
      aria-label="Add payer"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (name.trim().length >= 2 && kind !== null) add.mutate();
      }}
    >
      <div className={styles.row}>
        <TextField
          label="Name"
          value={name}
          onChange={setName}
          {...(tried && name.trim().length < 2 ? { errorMessage: 'Enter a name.' } : {})}
        />
        <Select
          label="Kind"
          placeholder="Choose a kind"
          options={(['insurer', 'corporate', 'other'] as const).map((k) => ({
            id: k,
            label: PAYER_KIND_LABELS[k],
          }))}
          value={kind}
          onChange={(value) => {
            setKind(value as 'insurer' | 'corporate' | 'other');
          }}
          {...(tried && kind === null ? { errorMessage: 'Choose a kind.' } : {})}
        />
      </div>
      {add.error === null ? null : (
        <Notice tone="error">{message(add.error, "It wasn't added. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={add.isPending} pendingLabel="Adding…">
          Add payer
        </Button>
        <Button
          variant="quiet"
          onPress={() => {
            onDone(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function termsFrom(
  method: SplitMethod,
  percentText: string,
  cents: bigint | null,
): SplitTermsView | string {
  switch (method) {
    case 'payer_pays_all':
    case 'patient_pays_all':
      return { method };
    case 'patient_copay_percent': {
      const bp = basisPointsFromPercent(percentText);
      if (!bp.ok || bp.value < 0 || bp.value > 10_000)
        return 'Enter a percentage from 0 to 100, like 20 or 12.5.';
      return { method, copayBasisPoints: bp.value };
    }
    case 'patient_copay_fixed_per_unit':
    case 'payer_limit_per_unit':
      if (cents === null) return 'Enter the amount per unit.';
      return { method, amountPerUnit: { currency: 'KES', cents: cents.toString() } };
  }
}

function AddRule({
  payer,
  rules,
  schemeOptions,
  onDone,
}: {
  payer: PayerView;
  rules: SplitRuleView[];
  schemeOptions: { id: string; label: string }[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [scheme, setScheme] = useState(EVERY_SCHEME);
  const [category, setCategory] = useState(EVERY_CATEGORY);
  const [method, setMethod] = useState<SplitMethod | null>(null);
  const [percentText, setPercentText] = useState('');
  const [cents, setCents] = useState<bigint | null>(null);
  const [tried, setTried] = useState(false);
  const schemeId = scheme === EVERY_SCHEME ? null : scheme;
  const categoryValue = category === EVERY_CATEGORY ? null : (category as ServiceItemCategory);
  // When the form opened: the rule current then is the one this change replaces.
  const [now] = useState(() => Date.now());
  const current =
    rules.find(
      (r) =>
        r.schemeId === schemeId &&
        r.category === categoryValue &&
        r.serviceItemId === null &&
        new Date(r.effectiveFrom).getTime() <= now,
    ) ?? null;
  const terms = method === null ? 'Choose who pays.' : termsFrom(method, percentText, cents);
  const save = useMutation({
    mutationFn: (value: SplitTermsView) =>
      changeSplitRule(payer.id, {
        schemeId,
        category: categoryValue,
        serviceItemId: null,
        terms: value,
        startsOn: 'now',
        expectedCurrentRuleId: current?.id ?? null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['payers', payer.id] });
      toasts.show({ title: 'Rule changed', description: payer.name });
      onDone();
    },
  });
  return (
    <form
      className={styles.inlineForm}
      aria-label="Change who pays"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (typeof terms !== 'string') save.mutate(terms);
      }}
    >
      <div className={styles.row}>
        <Select
          label="Scheme"
          options={[{ id: EVERY_SCHEME, label: 'Every scheme' }, ...schemeOptions]}
          value={scheme}
          onChange={(value) => {
            setScheme(String(value));
          }}
        />
        <Select
          label="Applies to"
          options={[
            { id: EVERY_CATEGORY, label: 'Every service and item' },
            ...SERVICE_ITEM_CATEGORIES.map((c) => ({ id: c, label: CATEGORY_LABELS[c] })),
          ]}
          value={category}
          onChange={(value) => {
            setCategory(String(value));
          }}
        />
      </div>
      <Select
        label="Who pays"
        placeholder="Choose who pays"
        options={SPLIT_METHODS.map((m) => ({ id: m, label: METHOD_LABELS[m] }))}
        value={method}
        onChange={(value) => {
          setMethod(value as SplitMethod);
        }}
        {...(tried && method === null ? { errorMessage: 'Choose who pays.' } : {})}
      />
      {method === 'patient_copay_percent' ? (
        <TextField
          label="Patient's share"
          unit="%"
          inputMode="decimal"
          value={percentText}
          onChange={setPercentText}
          {...(tried && typeof terms === 'string' ? { errorMessage: terms } : {})}
        />
      ) : null}
      {method === 'patient_copay_fixed_per_unit' || method === 'payer_limit_per_unit' ? (
        <MoneyField
          label={
            method === 'payer_limit_per_unit'
              ? 'Payer pays up to, per unit'
              : 'Patient pays, per unit'
          }
          value={cents}
          isRequired
          onChange={(value) => {
            setCents(value);
          }}
          {...(tried && typeof terms === 'string' ? { errorMessage: terms } : {})}
        />
      ) : null}
      {typeof terms === 'string' ? null : <p>{`From now: ${describeTerms(terms, payer.name)}.`}</p>}
      {current === null ? null : (
        <p className={styles.meta}>{`Replaces: ${describeTerms(current.terms, payer.name)}.`}</p>
      )}
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, "The rule wasn't changed. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
          Change rule
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Preview({
  payer,
  facilityId,
  schemeOptions,
}: {
  payer: PayerView;
  facilityId: string;
  schemeOptions: { id: string; label: string }[];
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const [scheme, setScheme] = useState(EVERY_SCHEME);
  const [line, setLine] = useState<{ item: ServiceItemView; quote: QuoteLineView } | null>(null);
  const trimmed = text.trim();
  const search = useQuery({
    queryKey: ['catalogue', 'items', { q: trimmed, status: 'active', preview: true }],
    queryFn: () => listItems({ q: trimmed, status: 'active' }, 10),
    enabled: trimmed.length >= 2 && online,
  });
  const run = useMutation({
    mutationFn: async (item: ServiceItemView) => {
      const result = await quote({
        facilityId,
        payerId: payer.id,
        schemeId: scheme === EVERY_SCHEME ? null : scheme,
        lines: [{ serviceItemId: item.id, quantity: 1 }],
      });
      const first = result.lines[0];
      if (first === undefined) throw new Error('empty quote');
      return { item, quote: first };
    },
    onSuccess: setLine,
  });
  const status = !online
    ? 'offline'
    : trimmed.length < 2
      ? 'idle'
      : search.isError
        ? 'error'
        : search.isFetching
          ? 'searching'
          : 'ready';
  return (
    <section className={styles.section} aria-labelledby="preview-heading">
      <div className={styles.sectionHeader}>
        <h2 id="preview-heading">Check a price</h2>
      </div>
      <div className={styles.row}>
        {schemeOptions.length > 0 ? (
          <Select
            label="Scheme"
            options={[{ id: EVERY_SCHEME, label: 'No scheme' }, ...schemeOptions]}
            value={scheme}
            onChange={(value) => {
              setScheme(String(value));
              setLine(null);
            }}
          />
        ) : null}
        <SearchPicker
          label="Service or item"
          description="Type part of the name or the code."
          inputValue={text}
          onInputChange={setText}
          status={status}
          items={search.data?.items ?? []}
          itemsQuery={trimmed}
          getKey={(item) => item.id}
          getTextValue={(item) => `${item.code} ${item.name}`}
          renderItem={(item) => (
            <span>
              <span className="wodi-id">{item.code}</span> {item.name}
            </span>
          )}
          onSelect={(item) => {
            run.mutate(item);
          }}
          emptyMessage="Nothing matches. Check the spelling, or search by code."
          errorMessage="The catalogue couldn't be searched. Try again."
          offlineMessage="Checking a price needs a connection to Wodi."
        />
      </div>
      {run.error === null ? null : (
        <Notice tone="error">
          {message(run.error, "The price couldn't be checked. Try again.")}
        </Notice>
      )}
      {line === null ? null : <p>{previewSentence(line.item, line.quote, payer.name)}</p>}
    </section>
  );
}

export function previewSentence(item: ServiceItemView, line: QuoteLineView, payerName: string) {
  switch (line.state) {
    case 'not_priced':
      return `${item.name}: not priced. ${line.message}.`;
    case 'no_split':
      return `${item.name}: ${money(line.unitPrice)}. ${line.message}.`;
    case 'priced':
      return `${item.name}: ${money(line.unitPrice)}. ${payerName} pays ${money(line.payerAmount)}. Patient pays ${money(line.patientAmount)}.`;
  }
}

function PayerDetail({ payerId, facilityId }: { payerId: string; facilityId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const payer = useQuery(payerQuery(payerId));
  const lists = useQuery(priceListsQuery);
  const assignments = useQuery(assignmentsQuery(payerId));
  const rules = useQuery(splitRulesQuery(payerId));
  const [addingScheme, setAddingScheme] = useState(false);
  const [schemeName, setSchemeName] = useState('');
  const [changingRule, setChangingRule] = useState(false);
  const canManagePayers = session !== null && can(session, 'payers.manage');
  const canManagePrices = session !== null && can(session, 'prices.manage');

  const addScheme = useMutation({
    mutationFn: () => createScheme(payerId, { name: schemeName.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['payers', payerId] });
      toasts.show({ title: 'Scheme added', description: schemeName.trim() });
      setAddingScheme(false);
      setSchemeName('');
    },
  });
  const review = useMutation({
    mutationFn: (input: { id: string; version: number }) => reviewScheme(input.id, input.version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payers', payerId] }),
  });
  const assign = useMutation({
    mutationFn: (priceListId: string | null) =>
      putAssignment({ payerId, schemeId: null, facilityId: null, priceListId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['price-list-assignments', payerId] });
      toasts.show({ title: 'Price list changed' });
    },
  });

  if (payer.isPending) return <LoadingBlock label="Loading the payer" rows={4} />;
  if (payer.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void payer.refetch()}>
            Try again
          </Button>
        }
      >
        {message(payer.error, "This payer couldn't be loaded. Try again.")}
      </Notice>
    );
  const detail = payer.data;
  const schemeOptions = detail.schemes
    .filter((s) => s.status === 'active')
    .map((s) => ({ id: s.id, label: s.name }));
  const payerWide = (assignments.data ?? []).find(
    (a) => a.schemeId === null && a.facilityId === null,
  );
  const activeLists = (lists.data ?? []).filter((l) => l.status === 'active');
  const schemeName_ = (id: string | null) =>
    id === null ? 'Every scheme' : (detail.schemes.find((s) => s.id === id)?.name ?? 'A scheme');

  return (
    <div className={styles.record}>
      <h2>{detail.name}</h2>
      {detail.kind === 'sha' ? (
        <Notice tone="info">
          SHA tariffs and who pays what for SHA aren't set up in Wodi yet. SHA charges will read
          "not set up" until the official tariff and rules are loaded.
        </Notice>
      ) : null}

      {detail.kind === 'cash' ? null : (
        <section className={styles.section} aria-labelledby="schemes-heading">
          <div className={styles.sectionHeader}>
            <h3 id="schemes-heading">Schemes</h3>
            {canManagePayers && detail.kind !== 'sha' && !addingScheme ? (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setAddingScheme(true);
                }}
              >
                Add scheme
              </Button>
            ) : null}
          </div>
          {detail.schemes.length === 0 ? (
            <p>
              {detail.kind === 'sha'
                ? 'SHA schemes appear here as eligibility checks return them.'
                : 'No schemes yet.'}
            </p>
          ) : null}
          <ul className={styles.recordList}>
            {detail.schemes.map((s) => (
              <li key={s.id} className={styles.recordItem}>
                <span>{s.name}</span>
                {s.externalCode === null ? null : <span className="wodi-id">{s.externalCode}</span>}
                {s.reviewState === 'needs_review' ? (
                  <Tag state="attention">Needs review</Tag>
                ) : null}
                {s.status === 'inactive' ? <Tag state="neutral">Not in use</Tag> : null}
                {s.reviewState === 'needs_review' && canManagePayers ? (
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      review.mutate({ id: s.id, version: s.version });
                    }}
                  >
                    Mark reviewed
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {addingScheme ? (
            <form
              className={styles.inlineForm}
              aria-label="Add scheme"
              onSubmit={(event) => {
                event.preventDefault();
                if (schemeName.trim().length >= 2) addScheme.mutate();
              }}
            >
              <TextField label="Scheme name" value={schemeName} onChange={setSchemeName} />
              {addScheme.error === null ? null : (
                <Notice tone="error">
                  {message(addScheme.error, "It wasn't added. Try again.")}
                </Notice>
              )}
              <div className={styles.actions}>
                <Button type="submit" variant="primary" isPending={addScheme.isPending}>
                  Add scheme
                </Button>
                <Button
                  variant="quiet"
                  onPress={() => {
                    setAddingScheme(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
        </section>
      )}

      <section className={styles.section} aria-labelledby="list-heading">
        <div className={styles.sectionHeader}>
          <h3 id="list-heading">Price list</h3>
        </div>
        {assignments.isPending || lists.isPending ? (
          <LoadingBlock label="Loading price lists" rows={1} />
        ) : (
          <Select
            label={`Prices ${detail.name} is charged`}
            description="Used for every scheme and facility unless set more narrowly."
            placeholder="No price list: nothing can be charged"
            options={activeLists.map((l) => ({ id: l.id, label: l.name }))}
            value={payerWide?.priceListId ?? null}
            isDisabled={!canManagePrices || !online || assign.isPending}
            onChange={(value) => {
              assign.mutate(String(value));
            }}
          />
        )}
        {assign.error === null ? null : (
          <Notice tone="error">{message(assign.error, "It wasn't changed. Try again.")}</Notice>
        )}
      </section>

      <section className={styles.section} aria-labelledby="rules-heading">
        <div className={styles.sectionHeader}>
          <h3 id="rules-heading">Who pays</h3>
          {canManagePayers && !changingRule ? (
            <Button
              dense
              isDisabled={!online}
              onPress={() => {
                setChangingRule(true);
              }}
            >
              Change rule
            </Button>
          ) : null}
        </div>
        {rules.isPending ? <LoadingBlock label="Loading rules" rows={2} /> : null}
        {rules.isSuccess && rules.data.length === 0 ? (
          <p>No rule yet, so nothing can be charged to this payer.</p>
        ) : null}
        <ul className={styles.recordList}>
          {(rules.data ?? []).map((r) => (
            <li key={r.id} className={styles.recordItem}>
              <span>{`${schemeName_(r.schemeId)}, ${
                r.category === null ? 'every service and item' : CATEGORY_LABELS[r.category]
              }`}</span>
              <span>{describeTerms(r.terms, detail.name)}</span>
              <span className={styles.meta}>
                {r.effectiveTo === null
                  ? `From ${day(r.effectiveFrom)}`
                  : `${day(r.effectiveFrom)} to ${day(r.effectiveTo)}`}
              </span>
            </li>
          ))}
        </ul>
        {changingRule ? (
          <AddRule
            payer={detail}
            rules={rules.data ?? []}
            schemeOptions={schemeOptions}
            onDone={() => {
              setChangingRule(false);
            }}
          />
        ) : null}
      </section>

      {detail.kind === 'insurer' ? (
        <section className={styles.section} aria-labelledby="benefits-heading">
          <div className={styles.sectionHeader}>
            <h3 id="benefits-heading">Benefits</h3>
          </div>
          <p className={styles.meta}>
            Limits and excess move part of the insurer's share to the patient; approvals hold a
            service until the insurer's answer is recorded.
          </p>
          <SchemeBenefits schemes={detail.schemes} canManage={canManagePayers} />
        </section>
      ) : null}

      <Preview payer={detail} facilityId={facilityId} schemeOptions={schemeOptions} />
    </div>
  );
}

export function PayersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'catalogue.read');
  const online = useOnline();
  const payers = useQuery({ ...payersQuery, enabled: allowed });
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);

  if (!allowed) {
    return (
      <>
        <PageHeader title="Payers" />
        <Notice tone="info">
          You don't have permission to see payers. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <SettingsPage
      title="Payers"
      {...(can(session, 'payers.manage') && !adding
        ? {
            actions: (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add payer
              </Button>
            ),
          }
        : {})}
    >
      {adding ? (
        <AddPayer
          onDone={(payerId) => {
            setAdding(false);
            if (payerId !== null) setChosen(payerId);
          }}
        />
      ) : null}
      {payers.isPending ? <LoadingBlock label="Loading payers" rows={3} /> : null}
      {payers.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void payers.refetch()}>
              Try again
            </Button>
          }
        >
          {message(payers.error, "Payers couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {payers.isSuccess ? (
        <Table aria-label="Payers">
          <TableHeader>
            <Column isRowHeader>Name</Column>
            <Column>Kind</Column>
            <Column>Status</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody items={payers.data} renderEmptyState={() => 'No payers yet.'}>
            {(p) => (
              <Row id={p.id}>
                <Cell>{p.name}</Cell>
                <Cell>{`${PAYER_KIND_LABELS[p.kind]}${p.isSystem ? ' (built in)' : ''}`}</Cell>
                <Cell>
                  <Tag state={p.status === 'active' ? 'active' : 'neutral'}>
                    {p.status === 'active' ? 'In use' : 'Not in use'}
                  </Tag>
                </Cell>
                <Cell>
                  <Button
                    dense
                    variant="quiet"
                    aria-label={`Open ${p.name}`}
                    onPress={() => {
                      setChosen(p.id);
                    }}
                  >
                    Open
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      {chosen === null ? null : <PayerDetail payerId={chosen} facilityId={session.facility.id} />}
    </SettingsPage>
  );
}
